import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { Line } from 'react-chartjs-2';
import { createDeferredChart } from '../charts/DeferredChart';
import {
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import { NutritionalAnalytics, NutritionalTrendData } from '../../api/nutrition';

const DeferredLine = createDeferredChart(Line);

interface NutritionTrendsPanelProps {
  trends: { data: NutritionalTrendData[]; analytics: NutritionalAnalytics } | null;
  chartInstanceKey?: string;
}

export default function NutritionTrendsPanel({ trends, chartInstanceKey = 'nutrition-trends' }: NutritionTrendsPanelProps) {
  const labels = useMemo(
    () => (trends?.data ?? []).map((d) => format(new Date(d.date), 'MMM dd')),
    [trends?.data],
  );

  if (!trends) {
    return (
      <div className="rounded-lg border border-gray-200 bg-slate-50 py-12 text-center text-sm text-slate-500">
        No trend data for the selected period.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-gray-200">
          <h3 className="mb-4 text-lg font-medium text-slate-900">Nutritional trends</h3>
          <div className="h-64">
            <DeferredLine
              chartKey={`${chartInstanceKey}-macros`}
              fallbackHeight={256}
              data={{
                labels,
                datasets: [
                  { label: 'Protein %', data: trends.data.map((d) => d.proteinPercentage), borderColor: 'rgb(59, 130, 246)', backgroundColor: 'rgba(59, 130, 246, 0.1)' },
                  { label: 'Fat %', data: trends.data.map((d) => d.fatPercentage), borderColor: 'rgb(16, 185, 129)', backgroundColor: 'rgba(16, 185, 129, 0.1)' },
                  { label: 'Fiber %', data: trends.data.map((d) => d.fiberPercentage), borderColor: 'rgb(245, 158, 11)', backgroundColor: 'rgba(245, 158, 11, 0.1)' },
                  { label: 'Carbohydrate %', data: trends.data.map((d) => d.carbohydratePercentage || 0), borderColor: 'rgb(168, 85, 247)', backgroundColor: 'rgba(168, 85, 247, 0.1)' },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: { y: { beginAtZero: true, title: { display: true, text: '%' } } },
              }}
            />
          </div>
        </div>
        <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-gray-200">
          <h3 className="mb-4 text-lg font-medium text-slate-900">Quality score trend</h3>
          <div className="h-64">
            <DeferredLine
              chartKey={`${chartInstanceKey}-quality`}
              fallbackHeight={256}
              data={{
                labels,
                datasets: [{
                  label: 'Quality Score',
                  data: trends.data.map((d) => d.qualityScore),
                  borderColor: 'rgb(139, 69, 19)',
                  backgroundColor: 'rgba(139, 69, 19, 0.1)',
                  fill: true,
                }],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                scales: { y: { min: 0, max: 100 } },
              }}
            />
          </div>
        </div>
      </div>

      <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-gray-200">
        <h3 className="mb-4 text-lg font-medium text-slate-900">Analytics summary</h3>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div>
            <h4 className="mb-3 font-medium text-slate-700">Averages</h4>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Protein</dt><dd>{Number(trends.analytics.averages.proteinPercentage || 0).toFixed(1)}%</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Fat</dt><dd>{Number(trends.analytics.averages.fatPercentage || 0).toFixed(1)}%</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Quality</dt><dd>{Number(trends.analytics.averages.qualityScore || 0).toFixed(1)}/100</dd></div>
            </dl>
          </div>
          <div>
            <h4 className="mb-3 font-medium text-slate-700">Trends</h4>
            <dl className="space-y-2 text-sm">
              {Object.entries(trends.analytics.trends).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <dt className="capitalize text-slate-500">{key}</dt>
                  <dd className="flex items-center gap-1">
                    {value === 'improving' ? <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" /> : value === 'declining' ? <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" /> : <span className="h-2 w-2 rounded-full bg-gray-400" />}
                    <span>{value}</span>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
          <div>
            <h4 className="mb-3 font-medium text-slate-700">Recommendations</h4>
            <ul className="space-y-2 text-sm text-slate-700">
              {trends.analytics.recommendations.map((rec, i) => (
                <li key={i} className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-500" />
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
